/**
 * Helpers for the big-screen display page's token mode (`?token=<jwt>`).
 *
 * A display token is a long-lived, event-scoped, read-only JWT minted by
 * `GET /events/:id/display-token` (Clerk-authenticated). Presenting it as a
 * `token` query param on API requests — or as Socket.IO handshake auth —
 * grants read access to that event's display data without a Clerk session,
 * so the link can run unattended on a TV or projector.
 */

/** Query-param name carrying the display token in page and API URLs. */
export const DISPLAY_TOKEN_PARAM = 'token';

/**
 * Merge the display token into a request's query params. Without a token
 * this is a pass-through, so callers can use one code path for both the
 * Clerk-authenticated and token modes.
 */
export function withDisplayToken(
  token: string | null | undefined,
  params?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!token) return params;
  return { ...params, [DISPLAY_TOKEN_PARAM]: token };
}

/**
 * Build the shareable display URL for an event from a minted token.
 * `mode` deep-links a display sub-mode (e.g. `slideshow`).
 */
export function buildDisplayUrl(
  origin: string,
  eventId: string,
  token: string,
  mode?: string,
): string {
  const base = `${origin}/events/${eventId}/display?${DISPLAY_TOKEN_PARAM}=${encodeURIComponent(token)}`;
  return mode ? `${base}&mode=${encodeURIComponent(mode)}` : base;
}

/** Per-query refetch intervals (ms) for the display page's polling. */
export interface DisplayPollIntervals {
  readonly teams: number;
  readonly leaderboard: number;
  readonly activity: number;
  readonly photos: number;
}

/** Fast fallback polling — carries the page while the socket is down. */
const FAST_POLL: DisplayPollIntervals = {
  teams: 15_000,
  leaderboard: 15_000,
  activity: 10_000,
  photos: 30_000,
};

/** Slow backup sweep while the socket delivers real-time invalidations. */
const BACKUP_POLL_INTERVAL_MS = 60_000;

const BACKUP_POLL: DisplayPollIntervals = {
  teams: BACKUP_POLL_INTERVAL_MS,
  leaderboard: BACKUP_POLL_INTERVAL_MS,
  activity: BACKUP_POLL_INTERVAL_MS,
  photos: BACKUP_POLL_INTERVAL_MS,
};

/**
 * Socket-first polling policy. While the socket is connected it pushes
 * invalidations in real time, so polling stretches to a 60s backup sweep;
 * disconnected (or still connecting), polling falls back to the fast
 * intervals so the screen stays fresh. staleTime on the queries can stay
 * short in both modes — `invalidateQueries` refetches active queries
 * regardless of staleTime, so socket pushes always land immediately.
 */
export function displayPollIntervals(connected: boolean): DisplayPollIntervals {
  return connected ? BACKUP_POLL : FAST_POLL;
}
